/**
 * Routes an external image URL (e.g. a company logo) through a CORS-enabled image
 * proxy so it can be safely drawn into a <canvas> for PDF/PNG export (html2canvas).
 *
 * A plain <img src="..."> can display a cross-origin image fine even without CORS
 * headers — but the browser's canvas security model blocks reading pixel data back
 * out of one ("tainted canvas"), so html2canvas silently skips it and the export
 * comes out without the logo. Our own API doesn't expose a proxy endpoint, so this
 * uses wsrv.nl (a free, widely-used image proxy/CDN — https://wsrv.nl) which fetches
 * the image server-side and re-serves it with `Access-Control-Allow-Origin: *`,
 * which html2canvas (with `useCORS: true`) can then load without tainting.
 *
 * data: URIs are already same-origin-safe for canvas use and are returned as-is.
 *
 * If/when the backend adds its own proxy endpoint, swap the implementation here —
 * every caller goes through this one function.
 */
export const getProxiedImageUrl = (url) => {
  if (!url || url.startsWith("data:")) return url;
  return `https://wsrv.nl/?url=${encodeURIComponent(url)}`;
};

/**
 * html2canvas `onclone` hook: swaps any `<img data-proxy-src="...">` in the cloned
 * document (the one html2canvas actually renders) over to its proxied, CORS-enabled
 * source. Pair with `<img src={original} data-proxy-src={getProxiedImageUrl(original)} />`
 * — the on-screen image keeps using `original` (already works without CORS), only the
 * export snapshot switches to the proxy.
 */
export const swapProxiedImagesForCapture = (clonedDoc) => {
  clonedDoc.querySelectorAll("img[data-proxy-src]").forEach((img) => {
    img.crossOrigin = "anonymous";
    img.src = img.getAttribute("data-proxy-src");
  });
};
